<template>
  <div class="notice-page">
    <div class="notice-header">
      <div>
        <h2>{{ ui.title }}</h2>
        <p>{{ ui.subtitle }}</p>
      </div>
      <div class="notice-count">{{ ui.total }} {{ total }}</div>
    </div>

    <div v-if="list && list.length" class="notice-list">
      <article v-for="item in list" :key="item.id" class="notice-card">
        <div class="notice-icon" :class="`type-${typeOf(item)}`">{{ iconOf(item) }}</div>
        <div class="notice-main">
          <div class="notice-topline">
            <div class="notice-title">{{ localizedTitle(item) }}</div>
            <div class="notice-meta">
              <span class="notice-tag">{{ typeLabel(item) }}</span>
              <span class="notice-time">{{ item.create_time || item.update_time || '-' }}</span>
            </div>
          </div>
          <div class="notice-content" v-html="localizedContent(item)"></div>
        </div>
      </article>
    </div>
    <div v-else class="notice-empty">{{ ui.empty }}</div>

    <div class="pager" v-if="total > queryForm.size">
      <el-pagination
        background
        layout="prev, pager, next"
        :total="total"
        :page-size="queryForm.size"
        :current-page="queryForm.page"
        @current-change="pageChange"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'NoticeCenter',
  data () {
    return {
      list: [],
      total: 0,
      queryForm: { page: 1, size: 10 }
    }
  },
  computed: {
    localeKey () {
      const l = String(this.$i18n.locale || '').toLowerCase()
      if (l.indexOf('ja') === 0) return 'ja'
      if (l.indexOf('en') === 0) return 'en'
      return 'zh'
    },
    ui () {
      const map = {
        zh: { title: '通知', subtitle: '账户、邀请、代理升级及资金相关消息', total: '共', empty: '暂无消息' },
        en: { title: 'Notifications', subtitle: 'Account, referral, agent upgrade and fund notifications', total: 'Total', empty: 'No notifications' },
        ja: { title: '通知', subtitle: '口座・紹介・代理昇格・資金に関するお知らせ', total: '合計', empty: '通知はありません' }
      }
      return map[this.localeKey]
    }
  },
  created () { this.getRecord() },
  methods: {
    getRecord () {
      this.$http.post('/api/user/noticeMessageList', this.queryForm).then(result => {
        this.list = (result && result.list) || []
        this.total = Number((result && result.total) || 0)
      })
    },
    pageChange (page) { this.queryForm.page = page; this.getRecord() },
    typeOf (item) {
      if (item && item.message_type) return item.message_type
      const text = `${(item && item.title) || ''} ${(item && item.content) || ''}`
      if (/升级|upgrade/i.test(text)) return 'upgrade'
      if (/注册|register/i.test(text)) return 'register'
      if (/入金|deposit/i.test(text)) return 'deposit'
      if (/出金|withdraw/i.test(text)) return 'withdrawal'
      if (/转账|transfer/i.test(text)) return 'transfer'
      if (/会员|member/i.test(text)) return 'membership'
      return 'system'
    },
    iconOf (item) {
      return { upgrade: '↑', register: '+', deposit: '↓', withdrawal: '↑', transfer: '⇄', membership: 'V', system: '!' }[this.typeOf(item)] || '!'
    },
    typeLabel (item) {
      const t = this.typeOf(item)
      const labels = {
        zh: { upgrade: '升级', register: '邀请', deposit: '入金', withdrawal: '出金', transfer: '转账', membership: '会员', system: '系统' },
        en: { upgrade: 'Upgrade', register: 'Referral', deposit: 'Deposit', withdrawal: 'Withdrawal', transfer: 'Transfer', membership: 'Member', system: 'System' },
        ja: { upgrade: '昇格', register: '紹介', deposit: '入金', withdrawal: '出金', transfer: '振替', membership: '会員', system: 'システム' }
      }
      return labels[this.localeKey][t] || labels[this.localeKey].system
    },
    localizedTitle (item) {
      const title = String((item && item.title) || '')
      const known = {
        直属用户注册成功: { zh: '直属用户注册成功', en: 'Direct Referral Registered', ja: '直属ユーザー登録完了' },
        代理等级升级: { zh: '代理等级升级', en: 'Agent Level Upgraded', ja: '代理レベル昇格' },
        注册成功: { zh: '注册成功', en: 'Registration Successful', ja: '登録完了' },
        入金成功: { zh: '入金成功', en: 'Deposit Successful', ja: '入金完了' },
        入金失败: { zh: '入金失败', en: 'Deposit Failed', ja: '入金失敗' },
        出金成功: { zh: '出金成功', en: 'Withdrawal Successful', ja: '出金完了' },
        出金失败: { zh: '出金失败', en: 'Withdrawal Failed', ja: '出金失敗' }
      }
      return (known[title] && known[title][this.localeKey]) || title || '-'
    },
    localizedContent (item) {
      const content = String((item && item.content) || '-')
      if (this.localeKey === 'zh') return content

      // 对新代理体系中最常见的两类消息做结构化翻译；未知历史消息保留原文，避免误译金额/订单数据。
      if (String(item.title || '') === '直属用户注册成功') {
        const m = content.match(/<strong>(.*?)<\/strong>.*?ID[：:]\s*([^）)<]+)/i)
        const name = m ? m[1] : '-'
        const id = m ? m[2] : '-'
        return this.localeKey === 'ja'
          ? `直属ユーザー<strong>${name}</strong>（ID：${id}）が登録しました。「紹介明細」で確認できます。`
          : `Your direct referral <strong>${name}</strong> (ID: ${id}) has registered. View the user in Invitation Details.`
      }
      if (String(item.title || '') === '代理等级升级') {
        const m = content.match(/<strong>(.*?)<\/strong>/i)
        const level = m ? m[1] : ''
        const levelMap = {
          三级代理: { en: 'Level 3 Agent', ja: '三級代理' },
          二级代理: { en: 'Level 2 Agent', ja: '二級代理' },
          一级代理: { en: 'Level 1 Agent', ja: '一級代理' }
        }
        const v = (levelMap[level] && levelMap[level][this.localeKey]) || level
        return this.localeKey === 'ja'
          ? `代理レベルが<strong>${v}</strong>に昇格しました。新しいコミッション率と次の条件は「プロモーションセンター」で確認できます。`
          : `Your agent level has been upgraded to <strong>${v}</strong>. Check the new commission rates and next requirement in the Promotion Center.`
      }

      const values = []
      content.replace(/<span[^>]*>(.*?)<\/span>/gi, (all, value) => {
        values.push(String(value || '').replace(/<[^>]+>/g, '').trim())
        return all
      })
      const title = String(item.title || '')
      if (title === '入金成功' && values.length >= 2) {
        return this.localeKey === 'ja'
          ? `${values[0]} に申請した <strong>${values[1]}</strong> の入金が承認され、現金口座に反映されました。`
          : `Your deposit of <strong>${values[1]}</strong>, submitted at ${values[0]}, was approved and credited to your cash account.`
      }
      if (title === '入金失败' && values.length >= 2) {
        const reason = values[2] || '-'
        return this.localeKey === 'ja'
          ? `${values[0]} に申請した <strong>${values[1]}</strong> の入金は承認されませんでした。理由：${reason}`
          : `Your deposit of <strong>${values[1]}</strong>, submitted at ${values[0]}, was not approved. Reason: ${reason}`
      }
      if (title === '出金成功' && values.length >= 2) {
        return this.localeKey === 'ja'
          ? `${values[0]} に申請した <strong>${values[1]}</strong> の出金が承認されました。`
          : `Your withdrawal of <strong>${values[1]}</strong>, submitted at ${values[0]}, was approved.`
      }
      if (title === '出金失败' && values.length >= 2) {
        const reason = values[2] || '-'
        return this.localeKey === 'ja'
          ? `${values[0]} に申請した <strong>${values[1]}</strong> の出金は承認されませんでした。理由：${reason}`
          : `Your withdrawal of <strong>${values[1]}</strong>, submitted at ${values[0]}, was not approved. Reason: ${reason}`
      }
      if (title === '注册成功') {
        const username = values[0] || '-'
        const userId = values[1] || '-'
        const trading = values[2] || '-'
        return this.localeKey === 'ja'
          ? `HeyMetaへの登録が完了しました。ユーザー名：<strong>${username}</strong>、ユーザーID：${userId}、取引口座：${trading}。取引パスワードは初回利用前に再設定してください。`
          : `Your HeyMeta account is ready. Username: <strong>${username}</strong>, User ID: ${userId}, Trading Account: ${trading}. Reset the trading password before first use.`
      }
      return content
    }
  }
}
</script>

<style scoped lang="scss">
.notice-page { min-height: calc(100vh - 90px); margin: 0 -38px; padding: 28px 38px 55px; color: #d8edf5; background: radial-gradient(circle at 72% 0, rgba(0,203,244,.08), transparent 32%); }
.notice-header { display:flex; align-items:flex-end; justify-content:space-between; margin-bottom:18px; }
.notice-header h2 { margin:0; color:#00ccff; font-size:20px; }
.notice-header p { margin:7px 0 0; color:#718da4; font-size:13px; }
.notice-count { color:#6e8da4; font-size:13px; }
.notice-list { display:grid; gap:14px; }
.notice-card { display:flex; gap:16px; padding:18px; border:1px solid rgba(0,204,255,.22); border-radius:8px; background:linear-gradient(145deg, rgba(7,35,53,.96), rgba(4,24,38,.96)); box-shadow:inset 0 0 24px rgba(0,204,255,.025); }
.notice-icon { width:46px; height:46px; flex:0 0 46px; border:1px solid rgba(0,204,255,.28); border-radius:50%; display:flex; align-items:center; justify-content:center; color:#1fd8ff; background:rgba(0,204,255,.08); font-size:21px; font-weight:700; }
.notice-icon.type-upgrade { color:#ffd069; border-color:rgba(255,208,105,.25); background:rgba(255,208,105,.06); }
.notice-icon.type-register { color:#42dba5; border-color:rgba(66,219,165,.25); background:rgba(66,219,165,.06); }
.notice-main { flex:1; min-width:0; }
.notice-topline { display:flex; align-items:flex-start; justify-content:space-between; gap:20px; padding-bottom:12px; border-bottom:1px solid rgba(255,255,255,.08); }
.notice-title { color:#eafbff; font-size:16px; font-weight:600; }
.notice-meta { display:flex; align-items:center; gap:10px; flex:0 0 auto; }
.notice-tag { padding:4px 10px; border:1px solid rgba(0,204,255,.25); border-radius:12px; color:#19d8ff; font-size:12px; background:rgba(0,204,255,.07); }
.notice-time { color:#617e94; font-size:12px; }
.notice-content { padding-top:13px; color:#a9c4d1; line-height:1.75; font-size:14px; word-break:break-word; }
.notice-content ::v-deep strong { color:#16d8ff; }
.notice-empty { padding:120px 0; text-align:center; color:#58778e; border:1px solid rgba(0,204,255,.14); background:rgba(5,25,39,.62); }
.pager { margin-top:20px; text-align:right; }
</style>
